import axios from 'axios';
import { Recipe } from '../types';
import { z } from 'zod';

const OPENROUTER_API_KEY = import.meta.env.VITE_OPENROUTER_API_KEY;
if (!OPENROUTER_API_KEY) {
  throw new Error('Missing VITE_OPENROUTER_API_KEY environment variable');
}

const api = axios.create({
  baseURL: 'https://openrouter.ai/api/v1',
  headers: {
    'Authorization': `Bearer ${OPENROUTER_API_KEY}`,
    'HTTP-Referer': window.location.href,
    'X-Title': 'WiskWiz',
  }
});

const ingredientSchema = z.object({
  name: z.string().min(1, "Ingredient name is required").transform(val => val.trim()),
  amount: z.string().min(1, "Amount is required").transform(val => val.trim()),
  unit: z.string().transform(val => {
    const normalized = val?.trim().toLowerCase() || '';
    return normalized || 'g';
  }),
  notes: z.string().optional().transform(val => val?.trim() ?? '')
}).strict();

const recipeSchema = z.object({
  title: z.string().min(1, "Title is required").transform(val => val.trim()),
  description: z.string().min(1, "Description is required").transform(val => val.trim()),
  ingredients: z.array(ingredientSchema).min(1, "At least one ingredient is required"),
  instructions: z.array(z.string().min(1, "Instructions cannot be empty")).min(1, "At least one instruction is required"),
  prepTime: z.string().min(1, "Prep time is required").transform(val => val.trim()),
  cookTime: z.string().min(1, "Cook time is required").transform(val => val.trim()),
  difficulty: z.string().min(1, "Difficulty level is required").transform(val => val.trim()),
  servings: z.number().int().positive("Servings must be a positive number"),
  totalTime: z.string().min(1, "Total time is required").transform(val => val.trim()),
  notes: z.array(z.string()).default([])
}).strict();

function sanitizeJsonString(text: string): string {
  // First, try to extract JSON from markdown if present
  text = text.replace(/```(?:json)?\n?|\n?```/g, '').trim();
  
  // Find the outermost JSON object
  const start = text.indexOf('{');
  const end = text.lastIndexOf('}');
  
  if (start === -1 || end === -1) {
    throw new Error('No valid JSON object found in response');
  }
  
  // Extract just the JSON object
  let jsonStr = text.slice(start, end + 1);
  
  // Pre-process the string to handle common issues
  jsonStr = jsonStr
    // Remove newlines and extra spaces
    .replace(/\s+/g, ' ')
    // Fix arrays with missing commas
    .replace(/"\s*\]\s*\[/g, '", ')
    .replace(/"\s*\]\s*"/g, '", "')
    // Fix trailing/leading commas in arrays
    .replace(/,\s*]/g, ']')
    .replace(/\[\s*,/g, '[')
    // Fix multiple consecutive commas
    .replace(/,\s*,/g, ',')
    // Fix property names without quotes
    .replace(/([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g, '$1"$2":')
    // Fix unquoted string values
    .replace(/:\s*([^",{\[\]\s][^,}\]]*?)([,}\]])/g, ':"$1"$2')
    // Fix escaped quotes
    .replace(/\\"/g, '"')
    .replace(/"{2,}/g, '"')
    // Remove invalid control characters
    .replace(/[\x00-\x1F\x7F-\x9F]/g, '')
    // Fix empty arrays
    .replace(/\[\s*\]/g, '[]')
    // Fix empty objects
    .replace(/{\s*}/g, '{}');

  // Final cleanup
  return jsonStr
    .replace(/,(\s*[}\]])/g, '$1') // Remove trailing commas
    .replace(/([}\]])\s*,\s*([}\]])/g, '$1$2'); // Fix consecutive closing brackets
}

async function generateWithPrompt(ingredients: string[], isRetry = false): Promise<Recipe> {
  const prompt = `Create a baking recipe using these ingredients: ${ingredients.join(', ')}.

CRITICAL: Your response must be a single, valid JSON object with this exact structure:

{
  "title": "Recipe Name",
  "description": "Brief description",
  "ingredients": [
    {
      "name": "ingredient name",
      "amount": "number",
      "unit": "g/ml/whole",
      "notes": "optional notes"
    }
  ],
  "instructions": [
    "Step 1",
    "Step 2"
  ],
  "prepTime": "X mins",
  "cookTime": "Y mins",
  "difficulty": "Easy/Intermediate/Advanced",
  "servings": 4,
  "totalTime": "Z mins",
  "notes": [
    "Tip 1",
    "Tip 2"
  ]
}

Rules:
1. Response MUST be valid JSON only
2. No text before or after the JSON
3. All arrays must have proper commas
4. All strings must be in double quotes
5. Use metric units (g, ml) where possible`;

  try {
    const response = await api.post('/chat/completions', {
      model: 'mistralai/mistral-7b-instruct',
      messages: [
        { 
          role: 'system', 
          content: 'You are a professional baker. Always respond with valid JSON only, no other text.' 
        },
        { role: 'user', content: prompt }
      ],
      temperature: isRetry ? 0.5 : 0.7,
      max_tokens: 1024,
      top_p: 0.9,
      frequency_penalty: 0.2,
    });

    const text = response.data.choices[0]?.message?.content;
    if (!text) {
      throw new Error('Empty response received from AI');
    }

    try {
      const jsonStr = sanitizeJsonString(text);
      const parsedData = JSON.parse(jsonStr);
      return validateRecipeData(parsedData);
    } catch (parseError) {
      console.error('JSON parsing error:', parseError);
      if (!isRetry) {
        throw parseError;
      }
      throw new Error('Failed to generate a valid recipe. Please try different ingredients.');
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.error || 'Failed to connect to AI service');
    }
    throw error;
  }
}

function validateRecipeData(data: unknown): Recipe {
  try {
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid recipe data received');
    }

    // Pre-process the data to handle common issues
    const processed = {
      ...data as Record<string, unknown>,
      ingredients: Array.isArray((data as any).ingredients)
        ? (data as any).ingredients.map((ing: any) => ({
            name: String(ing.name || '').trim(),
            amount: String(ing.amount || '0').trim(),
            unit: String(ing.unit || 'g').trim().toLowerCase(),
            notes: String(ing.notes || '').trim()
          }))
        : [],
      instructions: Array.isArray((data as any).instructions)
        ? (data as any).instructions.map((step: string) => String(step).trim())
        : [],
      servings: Number((data as any).servings) || 4,
      notes: Array.isArray((data as any).notes)
        ? (data as any).notes.map((note: string) => String(note).trim())
        : []
    };

    const result = recipeSchema.safeParse(processed);
    if (!result.success) {
      const errors = result.error.issues.map(issue => issue.message).join(', ');
      throw new Error(`Invalid recipe format: ${errors}`);
    }

    return result.data;
  } catch (error) {
    console.error('Validation error:', error);
    throw error;
  }
}

export async function generateRecipe(ingredients: string[]): Promise<Recipe> {
  if (!ingredients?.length) {
    throw new Error('Please provide at least one ingredient');
  }

  const cleanIngredients = ingredients
    .map(ing => ing.trim().toLowerCase())
    .filter(Boolean);

  if (cleanIngredients.length === 0) {
    throw new Error('Please provide valid ingredients');
  }

  try {
    return await generateWithPrompt(cleanIngredients, false);
  } catch (firstError) {
    console.warn('First attempt failed:', firstError);
    return await generateWithPrompt(cleanIngredients, true);
  }
}